import {
  DEFAULT_SITE_CONTENT, PERMISSION_NAMES, type ActionName, type AppData, type ArgsOf, type Me, type Member, type MemberDebts,
  type ResultOf, type RoleSummary,
} from '@hbac/contract';
import type { Transport } from './transport';

/**
 * In-memory backend for local UI work and for the e2e smoke test.
 *
 * It replaces the old behaviour where an empty WEB_APP_URL made every call
 * resolve `{}` after 800ms — which was not a mock, just the app failing in a
 * way that looked like a data bug. This one returns shaped data, so screens
 * render the states they are supposed to.
 *
 * Opt in explicitly with VITE_USE_MOCK=1; it is never a silent fallback.
 */

const MEMBERS: Member[] = [
  { memberId: '1', fullName: 'Nguyễn Văn An', email: 'an@example.com', bankCode: 'VCB', bankNumber: '0123456789', bankOwner: 'NGUYEN VAN AN', roles: [{ id: 1, name: 'Quản trị viên', isSystem: true }], active: true, status: 'active', genName: 'Gen 7', bankReady: true, avatarUrl: null, teamId: null },
  { memberId: '2', fullName: 'Trần Thị Bình', email: 'binh@example.com', bankCode: 'TCB', bankNumber: '9876543210', bankOwner: 'TRAN THI BINH', roles: [{ id: 2, name: 'Thủ quỹ', isSystem: false }], active: true, status: 'active', genName: 'Gen 7', bankReady: true, avatarUrl: null, teamId: null },
  { memberId: '3', fullName: 'Lê Minh Cường', email: 'cuong@example.com', bankCode: 'MB', bankNumber: '5555666677', bankOwner: 'LE MINH CUONG', roles: [], active: true, status: 'active', genName: 'Gen 7', bankReady: true, avatarUrl: null, teamId: null },
  { memberId: '4', fullName: 'Phạm Thu Dung', email: 'dung@example.com', bankCode: 'ACB', bankNumber: '1112223334', bankOwner: 'PHAM THU DUNG', roles: [], active: false, status: 'active', genName: 'Gen 6', bankReady: true, avatarUrl: null, teamId: null },
];

/** The signed-in mock user: the administrator. */
const ME: Me = { ...(MEMBERS[0] as Member), permissions: [...PERMISSION_NAMES] };

const ROLES: RoleSummary[] = [
  { id: 1, name: 'Quản trị viên', description: 'Toàn quyền.', isSystem: true, permissions: [...PERMISSION_NAMES], memberCount: 1 },
  { id: 2, name: 'Thủ quỹ', description: 'Duyệt khoản và theo dõi thanh toán.', isSystem: false, permissions: ['bills.approve', 'reports.view'], memberCount: 1 },
  { id: 3, name: 'Biên tập trang công khai', description: 'Viết bài và gửi thông báo.', isSystem: false, permissions: ['posts.manage', 'notifications.send'], memberCount: 0 },
];

const APP_DATA: AppData = {
  members: MEMBERS,
  teams: [
    { id: 1, name: 'Chủ nhiệm', description: '', sortOrder: 1, archivedAt: null, memberCount: 0 },
    { id: 2, name: 'Chuyên môn', description: '', sortOrder: 2, archivedAt: null, memberCount: 0 },
  ],
  banks: [
    { code: 'VCB', name: 'Vietcombank' },
    { code: 'TCB', name: 'Techcombank' },
    { code: 'MB', name: 'MB Bank' },
    { code: 'ACB', name: 'ACB' },
  ],
  settings: {
    configs: [
      { key: 'CLUB_NAME', value: 'HBAC' },
      { key: 'REMINDER_DAYS_THRESHOLD', value: 2 },
      { key: 'AUTO_REMINDER_TIME', value: 9 },
      { key: 'MAX_REMINDERS', value: 3 },
      { key: 'PUBLIC_APP_URL', value: 'https://hbac.io.vn/' },
    ],
    triggers: { autoReminder: true, adminReport: false },
  },
};

const DEBTS: MemberDebts = {
  totalDebt: 350000,
  debts: [
    { billId: 12, description: 'Tiền sân cầu lông tháng 6', amount: 200000, qrUrl: '', transferContent: 'HBAC 12 AN', payerName: 'Nguyễn Văn An', date: '15/06/2026', daysOverdue: 5, pending: false },
    { billId: 14, description: 'Nước uống buổi tập', amount: 150000, qrUrl: '', transferContent: 'HBAC 14 AN', payerName: 'Trần Thị Bình', date: '20/06/2026', daysOverdue: 0, pending: true },
  ],
  paidHistory: [
    { billId: 9, description: 'Phí thành viên Q2', amount: 500000, qrUrl: '', transferContent: 'HBAC 9 AN', payerName: 'Nguyễn Văn An', date: '01/04/2026', daysOverdue: 0, pending: false, paidDate: '03/04/2026' },
  ],
};

const RESPONSES: Partial<Record<ActionName, unknown>> = {
  login: { token: 'mock-token', profile: ME, appData: APP_DATA },
  getData: APP_DATA,
  getMyProfile: ME,
  resetPasswordWithLink: { token: 'mock-token', profile: ME, appData: APP_DATA },
  setMyAvatar: ME,
  removeMyAvatar: ME,
  sendPasswordResetLinks: { results: [], quotaHit: false },
  addMembers: { added: [], skipped: [] },
  listRoles: ROLES,
  getMyDebts: DEBTS,
  getMyUpcoming: {
    events: [{ eventId: 1, title: 'Họp CLB tháng 10', startsAt: '2026-10-20T18:30', location: 'D3-301', kind: 'meeting' }],
    tasks: [{ taskId: 1, title: 'Thiết kế poster HBAAC', projectName: 'Truyền thông HBAAC', dueDate: '2026-10-22' }],
  },
  // Sample khoản so the Duyệt and Thu/Chi screens can be looked at without a Worker (Plan BT).
  getMyCreatedBills: [
    { billId: '14', kind: 'split', description: 'Tiền sân cầu lông tháng 10', total: 1200000, date: '2026-10-20', status: 'OPEN', payerName: 'Nguyễn Văn An', createdByName: 'Nguyễn Văn An', unpaid: 2 },
    { billId: '13', kind: 'reimburse', description: 'In ấn tài liệu workshop SQL', total: 350000, date: '2026-10-18', status: 'OPEN', mailFailed: 2, payerName: 'Nguyễn Văn An', createdByName: 'Nguyễn Văn An', unpaid: 0 },
    { billId: '12', kind: 'split', description: 'Pizza buổi họp', total: 480000, date: '2026-10-05', status: 'PENDING_APPROVAL', payerName: 'Nguyễn Văn An', createdByName: 'Nguyễn Văn An', unpaid: 0 },
    { billId: '9', kind: 'split', description: 'Nước uống sự kiện', total: 220000, date: '2026-09-28', status: 'REJECTED', rejectionReason: 'Thiếu hoá đơn, bổ sung rồi tạo lại.', payerName: 'Nguyễn Văn An', createdByName: 'Nguyễn Văn An', unpaid: 0 },
    { billId: '7', kind: 'reimburse', description: 'Quà tặng diễn giả', total: 600000, date: '2026-09-12', status: 'DONE', payerName: 'Nguyễn Văn An', createdByName: 'Nguyễn Văn An', unpaid: 0 },
  ],
  getPendingBills: [
    { billId: '15', kind: 'split', tag: null, attachmentCount: 1, description: 'Thuê phòng họp', total: 300000, payerName: 'Lê Minh Cường', date: '2026-10-25', createdByName: 'Lê Minh Cường', members: [{ name: 'Nguyễn Văn An', amount: 100000 }, { name: 'Lê Minh Cường', amount: 200000 }] },
  ],
  getPendingPaymentsForOwner: [
    { billId: 14, memberId: '2', memberName: 'Trần Thị Bình', description: 'Tiền sân cầu lông tháng 10', amount: 300000, payerName: 'Nguyễn Văn An', payerId: '1', billDate: '2026-10-20', claimedAt: '2026-10-26T09:15:00+07:00' },
    { billId: 14, memberId: '3', memberName: 'Lê Minh Cường', description: 'Tiền sân cầu lông tháng 10', amount: 300000, payerName: 'Nguyễn Văn An', payerId: '1', billDate: '2026-10-20', claimedAt: null },
  ],
  getAllPendingPayments: [
    { billId: 14, memberId: '2', memberName: 'Trần Thị Bình', description: 'Tiền sân cầu lông tháng 10', amount: 300000, payerName: 'Nguyễn Văn An', payerId: '1', billDate: '2026-10-20', claimedAt: '2026-10-26T09:15:00+07:00' },
    { billId: 11, memberId: '3', memberName: 'Lê Minh Cường', description: 'Liên hoan Gen 7', amount: 150000, payerName: 'Trần Thị Bình', payerId: '2', billDate: '2026-10-10', claimedAt: null },
  ],
  getUnsettledShares: [
    { billId: 14, memberId: '4', memberName: 'Phạm Thu Dung', description: 'Tiền sân cầu lông tháng 10', amount: 300000, paidStatus: 'UNPAID', daysOverdue: 3 },
  ],
  getReimbursements: [
    { billId: 13, description: 'In ấn tài liệu workshop SQL', amount: 350000, date: '2026-10-18', requesterId: '3', requesterName: 'Lê Minh Cường', bankCode: 'MB', bankNumber: '5555666677', bankOwner: 'LE MINH CUONG', qrUrl: '', transferContent: 'HBAC hoan ung 13', tag: null, attachmentCount: 1 },
  ],
  getMyApprovals: {
    rows: [
      { billId: 15, kind: 'split', status: 'PENDING_APPROVAL', description: 'Thuê phòng họp', date: '2026-10-25', payerName: 'Lê Minh Cường', createdByName: 'Lê Minh Cường', what: 'approve', amount: 300000 },
      { billId: 14, kind: 'split', status: 'OPEN', description: 'Tiền sân cầu lông tháng 10', date: '2026-10-20', payerName: 'Nguyễn Văn An', createdByName: 'Nguyễn Văn An', what: 'confirm', shares: 2, amount: 600000, at: '2026-10-26T09:15:00+07:00' },
      { billId: 13, kind: 'reimburse', status: 'OPEN', description: 'In ấn tài liệu workshop SQL', date: '2026-10-18', payerName: 'Lê Minh Cường', createdByName: 'Lê Minh Cường', what: 'refund', amount: 350000 },
    ],
    total: 3,
    counts: { approve: 1, confirm: 1, refund: 1 },
    all: 3,
  },
  getBillDraft: null,
  getBillDetail: {
    billId: 14, kind: 'split', tag: null, attachments: [], canAttach: true, canMarkReimbursed: false, canRemind: true, canResend: true,
    canRetryMail: false, description: 'Tiền sân cầu lông tháng 10', total: 1200000, date: '2026-10-20', status: 'OPEN', splitType: 'EQUAL',
    payerId: '1', payerName: 'Nguyễn Văn An', createdByName: 'Nguyễn Văn An', createdAt: '2026-10-20T08:00:00+07:00',
    canApprove: false, canReject: false,
    timeline: [
      { kind: 'created', at: '2026-10-20T08:00:00+07:00', byName: 'Nguyễn Văn An' },
      { kind: 'claimed', at: '2026-10-26T09:15:00+07:00', subjectName: 'Trần Thị Bình' },
    ],
    splits: [
      { memberId: '2', memberName: 'Trần Thị Bình', amount: 300000, paidStatus: 'PENDING', claimedAt: '2026-10-26T09:15:00+07:00', canReview: true },
      { memberId: '3', memberName: 'Lê Minh Cường', amount: 300000, paidStatus: 'PENDING', claimedAt: '2026-10-26T10:00:00+07:00', canReview: true },
      { memberId: '4', memberName: 'Phạm Thu Dung', amount: 300000, paidStatus: 'UNPAID', canSettle: true },
      { memberId: '5', memberName: 'Hoàng Thị Em', amount: 300000, paidStatus: 'PAID', paidDate: '2026-10-22', canRevoke: true },
    ],
    canEditFully: false, canEditBasics: true, canCancel: true,
  },
  listBillTags: [],
  getPendingCounts: { ownerPending: 1, pendingBills: 0, adminPending: 0, unreadNotifications: 2, tasksForMe: 1, reimbursePending: 0, profileStamp: 'mock' },
  listMyNotifications: {
    items: [
      { id: 3, kind: 'ANNOUNCEMENT', title: 'Nộp minh chứng chi workshop SQL', body: 'Các bạn đã ứng tiền in ấn gửi ảnh hoá đơn trước thứ 6.', link: '/notifications', createdAt: '2026-09-25T03:00:00Z', read: false, readAt: null, requiresAck: true },
      { id: 2, kind: 'BILL_NEW', title: 'Khoản cần trả: Tiền sân cầu lông tháng 6', body: 'Bạn cần trả 200.000đ cho Nguyễn Văn An.', link: '/portal?bill=12', createdAt: '2026-09-25T02:00:00Z', read: false, readAt: null, requiresAck: false },
      { id: 1, kind: 'ANNOUNCEMENT', title: 'Họp CLB tối thứ 6', body: '19h tại D9.', link: '/notifications', createdAt: '2026-09-24T12:00:00Z', read: true, readAt: '2026-09-24T13:00:00Z', requiresAck: false },
    ],
    unread: 2,
    hasMore: false,
  },
  listAnnouncements: [],
  listAllPosts: [
    { id: 1, slug: 'bai-1', title: 'Tuyển thành viên khoá mới', excerpt: 'Tóm tắt', body: 'Nội dung', coverUrl: null, category: 'tin-tuc', publishedAt: '2026-10-21T09:00:00+07:00', authorName: 'Nguyễn Văn An', status: 'PUBLISHED' },
    { id: 2, slug: 'bai-2', title: 'Thông báo lịch sinh hoạt', excerpt: 'Tóm tắt', body: 'Nội dung', coverUrl: null, category: 'thong-bao', publishedAt: '2026-10-22T09:00:00+07:00', authorName: 'Nguyễn Văn An', status: 'PUBLISHED' },
    { id: 3, slug: 'bai-3', title: 'Kế hoạch sự kiện tháng 11', excerpt: 'Tóm tắt', body: 'Nội dung', coverUrl: null, category: 'su-kien', publishedAt: '2026-10-23T09:00:00+07:00', authorName: 'Nguyễn Văn An', status: 'DRAFT' },
    { id: 4, slug: 'bai-4', title: 'Bản nháp chưa đặt tên', excerpt: 'Tóm tắt', body: 'Nội dung', coverUrl: null, category: 'tin-tuc', publishedAt: '2026-10-24T09:00:00+07:00', authorName: 'Nguyễn Văn An', status: 'DRAFT', scheduledAt: '2026-11-01T08:00:00+07:00' },
  ],
  // Sample campaigns so the Gửi mail list and its Chọn can be looked at without a Worker (Plan BW).
  listCampaigns: [
    { id: 4, name: 'Mời họp tháng 10', subject: 'Thư mời', status: 'sending', total: 40, sent: 12, failed: 0, pending: 28, createdAt: '2026-10-26T09:00:00+07:00', createdByName: 'Nguyễn Văn An', updatedAt: '2026-10-27T09:00:00+07:00', lastSentAt: '2026-10-27T09:00:00+07:00', templateName: null },
    { id: 3, name: 'Tuyển thành viên', subject: 'Thư mời', status: 'sending', total: 120, sent: 60, failed: 2, pending: 58, createdAt: '2026-10-26T09:00:00+07:00', createdByName: 'Nguyễn Văn An', updatedAt: '2026-10-27T09:00:00+07:00', lastSentAt: '2026-10-27T09:00:00+07:00', templateName: null },
    { id: 2, name: 'Khảo sát cuối kỳ', subject: 'Thư mời', status: 'paused', total: 80, sent: 20, failed: 0, pending: 60, createdAt: '2026-10-26T09:00:00+07:00', createdByName: 'Nguyễn Văn An', updatedAt: '2026-10-27T09:00:00+07:00', lastSentAt: '2026-10-27T09:00:00+07:00', templateName: null },
    { id: 1, name: 'Bản nháp chưa gửi', subject: 'Thư mời', status: 'draft', total: 0, sent: 0, failed: 0, pending: 0, createdAt: '2026-10-26T09:00:00+07:00', createdByName: 'Nguyễn Văn An', updatedAt: '2026-10-27T09:00:00+07:00', lastSentAt: null, templateName: null },
  ],
  getMailBudget: { sent24h: 72, budget: 450, left: 378 },
  listMailTemplates: [],
  listMailForms: [],
  getAnnouncementReceipts: { announcementId: 1, read: [], unread: [] },
  emailAnnouncement: { sent: 0, failed: 0, pending: 0, stopped: null },
  listTasks: {
    rev: 1,
    projects: [
      { id: 1, name: 'Truyền thông HBAAC', description: 'Poster, video, fanpage', members: [{ memberId: '1', fullName: 'Nguyễn Văn An', role: 'manager' }], createdBy: { memberId: '1', fullName: 'Nguyễn Văn An' } },
    ],
    cards: [
      { id: 1, projectId: 1, title: 'Thiết kế poster HBAAC', status: 'doing', priority: 'high', dueDate: '2026-10-05', assignees: [{ memberId: '1', fullName: 'Nguyễn Văn An' }], createdBy: { memberId: '1', fullName: 'Nguyễn Văn An' }, updatedAt: '2026-09-26T02:00:00Z', doneAt: null, commentCount: 1, linkCount: 1 },
    ],
  },
  getTask: {
    task: { id: 1, projectId: 1, title: 'Thiết kế poster HBAAC', status: 'doing', priority: 'high', dueDate: '2026-10-05', assignees: [{ memberId: '1', fullName: 'Nguyễn Văn An' }], createdBy: { memberId: '1', fullName: 'Nguyễn Văn An' }, updatedAt: '2026-09-26T02:00:00Z', doneAt: null, commentCount: 1, linkCount: 1, description: 'Khổ A3.', links: [{ url: 'https://docs.google.com/presentation/d/x/edit', title: 'Bản nháp' }], createdAt: '2026-09-25T02:00:00Z' },
    activity: [{ id: 1, kind: 'created', by: { memberId: '1', fullName: 'Nguyễn Văn An' }, body: '', fromStatus: null, toStatus: null, at: '2026-09-25T02:00:00Z' }],
  },
  listBanksAdmin: [
    { code: 'VCB', name: 'Vietcombank', vietqrId: '970436', memberCount: 1 },
    { code: 'TCB', name: 'Techcombank', vietqrId: '970407', memberCount: 1 },
  ],
  getSiteContent: DEFAULT_SITE_CONTENT,
  getSiteContentForEdit: { content: DEFAULT_SITE_CONTENT, updated: { about: null, activities: null, recruitment: null, competition: null } },
  getDashboardData: {
    stats: { totalDebt: 1250000, overdueCount: 3, paidAmount: 800000 },
    overdueDebts: [
      { billId: 12, memberName: 'Lê Minh Cường', amount: 200000, description: 'Tiền sân cầu lông tháng 6', daysOverdue: 5 },
    ],
    byMember: [
      { memberId: '3', memberName: 'Lê Minh Cường', totalDebt: 700000 },
      { memberId: '2', memberName: 'Trần Thị Bình', totalDebt: 550000 },
    ],
    monthly: [],
    aging: [],
    reimburse: { owed: 450000, owedCount: 1, paidThisMonth: 0 },
    byTag: [{ kind: 'event', id: 1, name: 'HBAAC 2026', total: 450000, bills: 1 }],
  },
  getUnpaidDebtSummary: [
    { memberId: '3', memberName: 'Lê Minh Cường', totalDebt: 700000 },
  ],
  getLogs: {
    logs: [
      { timestamp: '24/09/2026 09:12', level: 'INFO', action: 'LOGIN', details: 'an@example.com', user: 'Nguyễn Văn An' },
      { timestamp: '24/09/2026 08:40', level: 'ERROR', action: 'MAIL_SEND', details: 'SMTP timeout', user: 'System' },
    ],
    statistics: { totalLogs: 2 },
  },
  createBill: { billId: 99, status: 'OPEN' },
};

export class MockTransport implements Transport {
  readonly name = 'mock';

  constructor(private readonly latencyMs = 250) {}

  async call<A extends ActionName>(action: A, args: ArgsOf<A>): Promise<ResultOf<A>> {
    await new Promise((r) => setTimeout(r, this.latencyMs));
    console.info(`[mock] ${action}`, args);
    // Actions with no fixture are writes; { success: true } matches their real shape. A fixture of null is kept (no draft).
    const canned = Object.prototype.hasOwnProperty.call(RESPONSES, action) ? RESPONSES[action] : { success: true };
    return canned as ResultOf<A>;
  }
}
